import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { generateRandomString } from "utils/helper";

import * as services from "../services/fundApi";
import * as slices from "../slices/fundSlice";

function toParams(params = {}) {
    const {
        pagination = {},
        sortBy = [],
    } = params;

    return {
        pagination: {
            page: pagination.page,
            size: pagination.pageSize,
        },
        sortBy: sortBy
            .filter((s) => s.order !== undefined)
            .map((s) => ({
                direction: s.order === "descend" ? "desc" : "asc",
                property: s.field,
            })),
    };
}

const useFundList = () => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isLoading = useSelector(
        (state) => state.fund.isLoading
    );

    const fundList = useSelector(
        (state) => state.fund.fundList
    );

    const currentCompany = useSelector(
        (state) => state.company.currentCompany
    );

    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const [sorter, setSorter] = useState([]);

    const [valuesChange, setValuesChange] = useState({});

    /**
     * Search
     */
    const onSubmit = useCallback(
        async (values = {}) => {
            try {
                dispatch(slices.setIsLoading(true));

                const response = await services.getFundList({
                    requestId: generateRandomString(),
                    companyId: currentCompany,

                    ...values,

                    page: 0,
                    size: 10,

                    sortBy: sorter,
                });

                dispatch(
                    slices.setFundList(
                        response?.data?.content || []
                    )
                );

                setValuesChange(values);

                setPagination({
                    page: 0,
                    size: 10,
                    current: 1,
                    pageSize: 10,
                    total: response?.data?.totalItems || response?.data?.totalElements || 0,
                });
            } catch (error) {
                console.error("getFundList error:", error);
            } finally {
                dispatch(slices.setIsLoading(false));
            }
        },
        [
            currentCompany,
            dispatch,
            sorter,
        ]
    );

    /**
     * Get Fund List
     */
    const getFundList = useCallback(
        async (params = {}) => {
            try {
                dispatch(slices.setIsLoading(true));

                const response = await services.getFundList({
                    requestId: generateRandomString(),
                    companyId: currentCompany,

                    ...valuesChange,
                    ...params.pagination,

                    sortBy: params.sortBy,
                });

                dispatch(
                    slices.setFundList(
                        response?.data?.content || []
                    )
                );

                setPagination((prev) => ({
                    ...prev,
                    page: response.data?.pageNo ?? response.data?.number + 1,
                    current: response.data?.pageNo ?? response.data?.number + 1,
                    pageNo: response.data?.pageNo ?? response.data?.number,
                    pageSize: response.data?.pageSize ?? response.data?.size,
                    total: response.data?.totalItems ?? response.data?.totalElements,
                }));
            } catch (error) {
                console.error("getFundList error:", error);
            } finally {
                dispatch(slices.setIsLoading(false));
            }
        },
        [
            currentCompany,
            dispatch,
            valuesChange,
        ]
    );

    /**
     * Table pagination / sorting
     */
    const handleOnChange = useCallback(
        (
            tablePagination,
            tableFilters,
            tableSorter,
            tableExtra
        ) => {
            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];

            setSorter(modifiedSorter);

            console.log("tablePagination", tablePagination);


            const modifiedParams = toParams({
                pagination: {
                    page:
                        tablePagination.current - 1,

                    pageSize:
                        tablePagination.pageSize,
                },

                sortBy: modifiedSorter,
            });

            getFundList(modifiedParams);
        },
        [getFundList]
    );

    /**
     * Edit
     */
    const onEdit = useCallback(
        (param) => {
            if (!param) return;

            navigate(`/configuration/payroll/fund/edit/${param.id}`);
        },
        [navigate]
    );

    /**
     * View
     */
    const onView = useCallback(
        (param) => {

            if (!param) return;

            navigate(`/configuration/payroll/fund/view/${param.id}`);
        },
        [navigate]
    );

    /**
     * Create
     */
    const onCreate = useCallback(() => {
        navigate("/configuration/payroll/fund/create");
    }, [navigate]);

    /**
     * Delete
     */
    const onDelete = useCallback(
        async (param) => {
            if (!param) return;

            try {
                dispatch(slices.setIsLoading(true));

                await services.deleteFund(param.id);

                await getFundList();
            } catch (error) {
                console.error("deleteFund error:", error);
            } finally {
                dispatch(slices.setIsLoading(false));
            }
        },
        [dispatch, getFundList]
    );

    /**
     * Clear search
     */
    const onClear = useCallback(() => {
        setValuesChange({});
        setSorter([]);

        getFundList({
            pagination: {
                page: 0,
                size: 10,
            },
            sortBy: [],
        });
    }, [getFundList]);

    /**
     * Initial load
     */
    useEffect(() => {
        if (currentCompany != null) {
            getFundList({
                pagination: {
                    page: 0,
                    size: 10,
                },
                sortBy: [],
            });
        }
    }, [currentCompany]);

    return {
        isLoading,
        fundList,
        pagination,

        onSubmit,
        onClear,

        onCreate,
        onView,
        onEdit,
        onDelete,

        onChange: handleOnChange,

        valuesChange,
        setValuesChange,
    };
};

export default useFundList;